import React from "react";
import type { Dealer } from ".././types/index";
import {
  dealerWorkshopIcon,
  customerWorkshopIcon,
  externalCompanyIcon,
} from "./MapsIcons";

const scaniaColor = dealerWorkshopIcon().fillColor ?? "#041E42";
const customerColor = customerWorkshopIcon().fillColor ?? "#ff6200ff";
const externalColor = externalCompanyIcon().fillColor ?? "#00811aff";

interface WorkshopItemProps {
  dealer: Dealer;
  isExpanded: boolean;
  isSelected: boolean;
  isClosest?: boolean;
  onToggle: () => void;
  refreshUI: number;
  // NEW: allow WorkshopList to pass refs/classes for highlight/scroll
  itemRef?: React.Ref<HTMLLIElement>;
  className?: string;
}

const WorkshopItem: React.FC<WorkshopItemProps> = ({
  dealer,
  isExpanded,
  isSelected,
  isClosest,
  onToggle,
  refreshUI,
  itemRef,
  className,
}) => {
  const [showServices, setShowServices] = React.useState(false);

  return (
    <li
      key={`${dealer.dealerId}-${refreshUI}`}
      ref={itemRef}
      className={className}
      style={{
        marginBottom: "0.7rem",
        border: isClosest
          ? "2px solid #5ecb5e"
          : isSelected
            ? "2px solid #005fcc"
            : "1px solid #e0e0e0",
        borderRadius: "6px",
        padding: "0.5rem 0.7rem",
        backgroundColor: isClosest
          ? "#f0fff0"
          : isSelected
            ? "#e8f0fe"
            : "#fafbfc",
        fontSize: "13px",
        boxShadow: isClosest ? "0 1px 4px rgba(94,203,94,0.07)" : "none",
        transition: "background 0.25s ease, border-color 0.25s ease",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          cursor: "pointer",
          fontWeight: 700,
          marginBottom: "0.18rem",
          color: "#002663",
          fontSize: "13.2px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.25rem",
        }}
      >
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              color: "#002663",
              display: "flex",
              alignItems: "center",
              gap: 6,
              wordBreak: "break-word",
            }}
          >
            {dealer.name}
            {isSelected && (
              <span
                style={{
                  fontSize: "10px",
                  padding: "2px 5px",
                  backgroundColor: "#005fcc",
                  color: "#fff",
                  borderRadius: "6px",
                  marginLeft: "6px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Selected
              </span>
            )}
          </div>
          {dealer.city && (
            <div style={{ fontSize: "12px", color: "#5b6d8a", fontWeight: 400 }}>
              {dealer.city}
            </div>
          )}
          {/* NEW: show workshop ID */}
          {dealer.dealerId && (
            <div style={{ fontSize: "11px", color: "#6b7280" }}>
              WS-ID: {dealer.dealerId}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.4rem",
            alignItems: "center",
            flex: "0 0 auto",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          {dealer.distanceText && (
            <span style={{ fontWeight: 400, color: "#444" }}>
              {dealer.distanceText}
              {isClosest && " ✅"}
            </span>
          )}
          {dealer.durationText && (
            <span style={{ fontWeight: 400, color: "#888", fontSize: "12px" }}>
              Driving Time: {dealer.durationText}
            </span>
          )}
          {dealer.source && (
            <span
              style={{
                fontSize: "11px",
                padding: "2px 6px",
                borderRadius: "10px",
                backgroundColor:
                  dealer.source === "Scania"
                    ? scaniaColor
                    : dealer.source === "External"
                      ? externalColor
                      : dealer.source === "Customer"
                        ? customerColor
                        : "#cccccc",
                color: "#fff",
                fontWeight: 500,
                letterSpacing: "0.02em",
                textTransform: "capitalize",
              }}
            >
              {dealer.source}
            </span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div
          style={{
            backgroundColor: "#f3f8ff",
            border: "1px solid #d6e6ff",
            borderRadius: "4px",
            padding: "0.7rem 0.8rem",
            marginTop: "0.3rem",
            fontSize: "13px",
            lineHeight: 1.5,
            color: "#222",
            boxShadow: "0 1px 2px rgba(0,38,99,0.03)",
            wordBreak: "break-word",
            overflow: "visible",
          }}
        >
          <p style={{ margin: 0 }}>
            <strong style={{ color: "#002663" }}>Address:</strong>{" "}
            {dealer.address || "Unknown"}
          </p>
          <p style={{ margin: "0.4rem 0 0" }}>
            <strong style={{ color: "#002663" }}>Phone:</strong>{" "}
            {dealer.phone || "Not available"}
          </p>
          <p style={{ margin: "0.4rem 0 0" }}>
            <strong style={{ color: "#002663" }}>Distance:</strong>{" "}
            {dealer.distanceText || "N/A"}
          </p>
          <p style={{ margin: "0.4rem 0 0" }}>
            <strong style={{ color: "#002663" }}>Driving Time:</strong>{" "}
            {dealer.durationText ??
              (dealer.distanceText ? "Calculating..." : "N/A")}
          </p>

          {dealer.openingHours && dealer.openingHours.length > 0 && (
            <div style={{ marginTop: "0.5rem" }}>
              <p style={{ margin: "0.3rem 0 0" }}>
                <strong style={{ color: "#002663" }}>Opening Hours:</strong>
              </p>
              <ul style={{ paddingLeft: "1.1rem", margin: "0.15rem 0 0" }}>
                {dealer.openingHours.map((entry, idx) => (
                  <li key={idx}>
                    {entry.days.join(", ")}:{" "}
                    {entry.openTimes
                      .map((t) => `${t.timeFrom}–${t.timeTo}`)
                      .join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: "0.4rem" }}>
            <div
              onClick={() => setShowServices((prev) => !prev)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                backgroundColor: "#e8f0fe",
                borderRadius: "4px",
                padding: "4px 8px",
              }}
            >
              <strong style={{ color: "#002663" }}>Services</strong>
              <span style={{ fontSize: "1rem", color: "#002663" }}>
                {showServices ? "▲" : "▼"}
              </span>
            </div>

            {showServices && (
              <div
                style={{
                  maxHeight: "38vh",
                  overflowY: "auto",
                  paddingRight: 6,
                  marginTop: "0.25rem",
                  border: "1px solid #e6f0ff",
                  borderRadius: 6,
                  background: "#ffffff",
                }}
              >
                <ul
                  style={{
                    paddingLeft: "1.1rem",
                    margin: "0.15rem 0 0",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.4,
                  }}
                >
                  {dealer.serviceCodes.length > 0 ? (
                    dealer.serviceCodes
                      .slice() // clone array to avoid mutation
                      .sort((a, b) => a.localeCompare(b)) // alphabetical order
                      .map((code) => (
                        <li key={code} style={{ marginBottom: "0.25rem" }}>
                          {code}
                        </li>
                      ))
                  ) : (
                    <li>No listed services</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <div style={{ marginTop: "0.6rem" }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const wsId = dealer.dealerId;
                const country = dealer.countryCode || dealer.country || ""; // Dealer CC from backend i.e. SIS
                const url = `https://sisupdate.scania.com/ses?dealerid=${wsId}&country=${country}&sesuserid=1554c073-7f26-4c58-b55b-6a8f9e2484e1`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              style={{
                padding: "6px 10px",
                borderRadius: "4px",
                border: "1px solid #005fcc",
                backgroundColor: "#005fcc",
                color: "#fff",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Open in SIS
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

export default WorkshopItem;